import Loading from "@builder/components/app/Loading";
import PopupHeader from "@builder/components/app/Popup/PopupHeader";
import Button from "@builder/components/general/Button";
import Icon from "@builder/components/general/Icon";
import Input from "@builder/components/general/Input";
import OliverText from "@builder/components/general/OliverText";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { BuildTypes } from "@common/build/types";
import { useEffect, useState } from "react";
// import { useTranslation } from "react-i18next";
import SimpleCheckbox from "@builder/components/general/SimpleCheckbox";
import ChangelogTextArea from "@builder/components/general/TextArea/ChangelogTextArea";
import useSaveBuildSettingsKey from "@builder/hooks/useSaveBuildSettingsKey";
import { PublishDetails, useGetHighestPublishedVersionQuery, usePublishBuildMutation } from "@builder/store/slices/BuildsApiSlice/buildsApiSlice";
import { closePopup } from "@builder/store/slices/popupSlice";
import { TimePicker } from "antd";
import DatePicker from "antd/es/date-picker";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import "./style.scss";
import { GettinPublished, MIN_TIME_TO_ENJOY_PUBLISHING_ANIMATION } from "./GettingPublished";
import { skipToken } from "@reduxjs/toolkit/query";

const PublishBuild = () => {
    const dispatch = useAppDispatch();
    const build = useAppSelector(state => state.builder.build);
    const user = useAppSelector(state => state.storeData.user);
    const [newVersion, setNewVersion] = useState<number[]>([0, 0, 1]);
    const { isLoading: saveBSKLoading, save: saveBSK } = useSaveBuildSettingsKey();
    const [publishBuild, { isLoading: publishBuildLoading }] = usePublishBuildMutation();
    const companyId = useAppSelector(state => state.builder.build.companyId);
    const { t } = useTranslation();
    const [isInfoExpanded, setIsInfoExpanded] = useState<boolean>(false);
    const [manualUpdate, setManualUpdate] = useState<boolean>(true);
    const [immediateUpdate, setImmediateUpdate] = useState<boolean>(false);
    const [scheduledTime, setScheduledTime] = useState<Dayjs | null>(null);
    const [scheduledDate, setScheduledDate] = useState<Dayjs | null>(null);
    const [notifyUser, setNotifyUser] = useState<boolean>(false);

    const { data: latestPublishedVersion, isLoading: isLoadingHighestPublishedVersion } = useGetHighestPublishedVersionQuery(
        companyId && build.id ? { companyId, buildId: build.id } : skipToken,
        { refetchOnMountOrArgChange: true }
    );

    const currentVersion = latestPublishedVersion || [0, 0, 0];

    useEffect(() => {
        if (latestPublishedVersion) {
            setNewVersion([latestPublishedVersion[0], latestPublishedVersion[1], latestPublishedVersion[2] + 1]);
        }
    }, [latestPublishedVersion]);

    const versionValidation = (index: number, value: string): number[] | undefined => {
        if (value.length <= 3 && (/^-?\d+$/.test(value) || value === "")) {
            const newVersionCopy = [...newVersion];
            newVersionCopy[index] = value === "" ? 0 : parseInt(value);
            if (newVersionCopy[0] > currentVersion[0]) {
                return newVersionCopy;
            } else if (newVersionCopy[0] === currentVersion[0]) {
                if (newVersionCopy[1] > currentVersion[1]) {
                    return newVersionCopy;
                } else if (newVersionCopy[1] === currentVersion[1]) {
                    if (newVersionCopy[2] > currentVersion[2]) {
                        return newVersionCopy;
                    }
                }
            }
        }
    };

    const versionCallback0 = (value: string) => {
        const newValue = versionValidation(0, value);
        if (newValue) {
            setNewVersion(newValue);
        }
    };
    const versionCallback1 = (value: string) => {
        const newValue = versionValidation(1, value);
        if (newValue) {
            setNewVersion(newValue);
        }
    };
    const versionCallback2 = (value: string) => {
        const newValue = versionValidation(2, value);
        if (newValue) {
            setNewVersion(newValue);
        }
    };

    //Changelog
    const [changelogValue, setChangelogValue] = useState<string>("");
    const changelogCallback = (value: string) => {
        setChangelogValue(value);
    };

    // Toast handler
    const handleToast = () => {
        if (toastInfo) {
            toast[toastInfo.status](toastInfo.message);
            if (toastInfo.status === "success") {
                dispatch(closePopup());
            }
            setToastInfo(null);
        }
    };

    const [fakePublishing, setFakePublishing] = useState(false);
    const [toastInfo, setToastInfo] = useState<{ status: "success" | "error"; message: string } | null>(null);

    useEffect(() => {
        if (fakePublishing) {
            const timer = setTimeout(() => {
                setFakePublishing(false);
                handleToast();
            }, MIN_TIME_TO_ENJOY_PUBLISHING_ANIMATION);
            return () => clearTimeout(timer);
        }
    }, [fakePublishing]);

    useEffect(() => {
        if (toastInfo && !fakePublishing && !publishBuildLoading) {
            handleToast();
        }
    }, [toastInfo, fakePublishing, publishBuildLoading]);

    //Publish
    const handlePublish = async () => {
        if (companyId) {
            // Validate scheduled time hasn't expired
            if (!manualUpdate && !immediateUpdate && scheduledDate && scheduledTime) {
                const scheduledDateTime = scheduledDate
                    .hour(scheduledTime.hour())
                    .minute(scheduledTime.minute())
                    .second(scheduledTime.second())
                    .millisecond(0);
                const now = dayjs();

                if (scheduledDateTime.isBefore(now)) {
                    toast.error(
                        t("popup.publishBuild.scheduledTimeExpired") || "The scheduled time has already passed. Please select a future date and time."
                    );
                    return;
                }
            }

            let scheduleTime = null;
            if (!manualUpdate && !immediateUpdate && scheduledDate && scheduledTime) {
                const date = scheduledDate.format("YYYY-MM-DD");
                const time = scheduledTime.format("HH:mm:ss");
                scheduleTime = `${date}T${time}`;
            }

            const data: PublishDetails = {
                version: newVersion.join("."),
                changelog: changelogValue,
                updateSettings: {
                    notifyUser: manualUpdate ? notifyUser : false,
                    autoUpdate: immediateUpdate || (!manualUpdate && Boolean(scheduledDate) && Boolean(scheduledTime)),
                    scheduleTime: scheduleTime || undefined
                }
            };

            try {
                setFakePublishing(true);
                console.log("publishDetails", data);
                await publishBuild({ companyId, build, publishDetails: data }).unwrap();

                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: "flow_published_update",
                        flow_id: build.id,
                        flow_name: build.name || "",
                        flow_version: data.version,
                        previous_version: currentVersion.join("."),
                        org_id: companyId,
                        user_id: user?._id || "",
                        user_email: user?.email || "",
                        published_at: new Date().toISOString()
                    });
                }

                saveBSK();
                setToastInfo({ status: "success", message: t("popup.publishBuild.publishSuccess") });
            } catch (error) {
                setToastInfo({ status: "error", message: t("popup.publishBuild.publishError") });
            }
        }
    };

    if (isLoadingHighestPublishedVersion || saveBSKLoading) {
        return (
            <div className="loading-publish-wrapper">
                <Loading notFullscreen text="Loading publish data" />
            </div>
        );
    } else if (publishBuildLoading || fakePublishing) {
        return <GettinPublished isPromiseFinished={!publishBuildLoading} />;
    } else {
        return (
            <div className="publish-build-wrapper">
                <PopupHeader src="/rocket-icon.svg" title="Publish your build" />
                <div className="publish-build-body">
                    {![BuildTypes.GLOBAL_BLOCK, BuildTypes.ORG_GLOBAL_BLOCK, BuildTypes.OLIVER_GLOBAL_BLOCK].includes(build.buildType!) && (
                        <div className="version-row">
                            <div className="multi-input-row">
                                <OliverText text="Current Version" size="xs" className="version-label" />
                                {currentVersion.map((version, i) => (
                                    <>
                                        <Input
                                            key={version}
                                            value={version.toString()}
                                            callback={() => { }}
                                            className="version-input"
                                            wrapperClassName="version-input-wrapper"
                                            disabled
                                        />
                                        {i < currentVersion.length - 1 && <OliverText text="." />}
                                    </>
                                ))}
                            </div>
                            <Icon src="/arrow-right-icon.svg" className="version-icon" containerStyle={{ height: "24px" }} />
                            <div className="multi-input-row">
                                <OliverText text="New Version" size="xs" className="version-label" translatePrefix="popup.publishBuild." />
                                {newVersion.map((version, i) => (
                                    <>
                                        <Input
                                            key={version}
                                            value={version.toString()}
                                            callback={[versionCallback0, versionCallback1, versionCallback2][i]}
                                            className="version-input"
                                            wrapperClassName="version-input-wrapper"
                                        />
                                        {i < newVersion.length - 1 && <OliverText text="." />}
                                    </>
                                ))}
                            </div>
                        </div>
                    )}

                    <ChangelogTextArea
                        value={changelogValue}
                        callback={changelogCallback}
                        wrapperClassName="publish-text-area"
                        placeholder="• change number 1"
                        label="Changelog"
                    />
                    <>
                        <OliverText text="Publish settings:" size="s" weight="medium" />
                        <div className="update-settings-wrapper">
                            <SimpleCheckbox
                                label="Manual update"
                                value="manual-update"
                                checked={manualUpdate}
                                callback={() => {
                                    setManualUpdate(!manualUpdate);
                                    if (!manualUpdate) {
                                        setImmediateUpdate(false);
                                        setScheduledDate(null);
                                        setScheduledTime(null);
                                    }
                                }}
                                custom={false}
                            />
                            <OliverText
                                size="xs"
                                weight="light"
                                color="base-grey"
                                className="setting-description"
                                text="By activating this feature, the version can only be upgraded through version control in Station Home."
                            />
                            {manualUpdate && (
                                <div className="notify-user-wrapper">
                                    <SimpleCheckbox
                                        label="Notify users"
                                        value="notify-users"
                                        checked={notifyUser}
                                        callback={(_, value) => setNotifyUser(value)}
                                        custom={true}
                                    />
                                    <OliverText
                                        size="xxs"
                                        weight="light"
                                        color="base-grey"
                                        text="This will send a notification to your users, and they can choose to update it or ignore it for now."
                                    />
                                </div>
                            )}
                        </div>
                        <div className="update-settings-wrapper">
                            <SimpleCheckbox
                                label="Scheduled update"
                                value="scheduled-update"
                                checked={!manualUpdate}
                                callback={() => setManualUpdate(!manualUpdate)}
                                custom={false}
                            />
                            <OliverText
                                size="xs"
                                weight="light"
                                color="base-grey"
                                className="setting-description"
                                text="Activating this feature will automatically update your flow on each station running this flow at the scheduled local time, which may be after work hours."
                            />
                        </div>
                        {!manualUpdate && (
                            <>
                                <div className="editable-info">
                                    <div className="info-header" onClick={() => setIsInfoExpanded(!isInfoExpanded)}>
                                        <div className="info-header-text">
                                            <img src="/info-icon.svg" />
                                            <OliverText text="How it works?" size="xs" weight="medium" />
                                        </div>
                                        <img src="/angled-arrow-down.svg" className={isInfoExpanded ? "active" : "inactive"} />
                                    </div>
                                    {isInfoExpanded && (
                                        <OliverText
                                            text="For kiosks, it will update automatically if the kiosk has been inactive for 2 minutes. For other types, it will show a popup if there have been no checkouts running for 2 minutes, or update as soon as the user navigates to the station home."
                                            size="xs"
                                            weight="light"
                                            color="base-grey"
                                            className="info-description"
                                        />
                                    )}
                                </div>
                                <div className="date-time-wrapper">
                                    <DatePicker
                                        value={scheduledDate}
                                        onChange={date => setScheduledDate(date)}
                                        format="MM/DD/YYYY"
                                        placeholder="Select date"
                                        disabled={immediateUpdate}
                                        rootClassName="date-time-item"
                                        disabledDate={current => {
                                            const today = dayjs().startOf("day");
                                            return current && current.isBefore(today, "day");
                                        }}
                                    />
                                    <TimePicker
                                        value={scheduledTime}
                                        onChange={time => setScheduledTime(time)}
                                        format="HH:mm"
                                        placeholder="Select time"
                                        rootClassName="date-time-item"
                                        disabled={immediateUpdate}
                                        disabledTime={() => {
                                            const now = dayjs();
                                            const today = dayjs().startOf("day");

                                            if (scheduledDate && scheduledDate.isSame(today, "day")) {
                                                return {
                                                    disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
                                                    disabledMinutes: (selectedHour: number) => {
                                                        if (selectedHour === now.hour()) {
                                                            return Array.from({ length: now.minute() }, (_, i) => i);
                                                        }
                                                        return [];
                                                    }
                                                };
                                            }
                                            return {};
                                        }}
                                    />
                                </div>
                                <div className="immediate-update-wrapper">
                                    <SimpleCheckbox
                                        label="Immediately"
                                        value="immediate-update"
                                        checked={immediateUpdate}
                                        callback={(_, value) => setImmediateUpdate(value)}
                                        custom={true}
                                    />
                                </div>
                            </>
                        )}
                    </>
                </div>
                <div className="publish-footer">
                    <Button
                        buttonStyle="primary"
                        icon={{ src: "/rocket-icon.svg" }}
                        text="Publish"
                        className="publish-button"
                        onClick={handlePublish}
                        disabled={!manualUpdate && !immediateUpdate && (!scheduledDate || !scheduledTime)}
                    />
                </div>
            </div>
        );
    }
};

export default PublishBuild;
